import { TableData, TableHeader } from "../Table/DataTable";
import StructureComponent from "./GridComponentTypes/StructureComponent";
import { ChangeEvent, Dispatch, forwardRef, ReactNode, SetStateAction, useEffect, useState } from "react";
import PlotComponent from "../Popups/ModelDetailsPopupComponents/PlotComponent";
import StatsValue from "./GridComponentTypes/StatsValue";
import DetailsModal from "../Popups/DetailsModal";
import CustomCheckbox from "../Form/CustomCheckbox";
import { GridComponents, VirtuosoGrid } from "react-virtuoso";
import { Skeleton, useMediaQuery, useTheme } from "@mui/material";

interface VirtualGridComponentContext {
    isLoading: boolean,
    itemWidth: string,
}

const gridComponents: GridComponents<VirtualGridComponentContext> = {
    List: forwardRef(({ style, children, ...props }, ref) => {
        const { isLoading, itemWidth } = props.context;
        return (
            <div
                ref={ref}
                {...props}
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    ...style,
                }}
            >
                {isLoading ? (
                    <>
                    {Array.from(Array(8).keys()).map(k => (
                        <div
                            style={{
                                padding: "0.5rem",
                                width: itemWidth,
                            }}
                        >
                            <Skeleton variant='rounded' width='100%' height={400}/>
                        </div>
                    ))}
                    </>
                ) : (
                    <>{children}</>
                )}
                
            </div>
        )
    }),
    Item: ({ children, ...props }) => {
        const { itemWidth } = props.context;
        return (
            <div
                {...props}
                style={{
                    padding: "0.5rem",
                    width: itemWidth,
                    display: "flex",
                    flex: "none",
                    alignContent: "stretch",
                    boxSizing: "border-box",
                }}
                >
                {children}
            </div>
        )
    }
} 

const ItemWrapper = ({ children, ...props }: { children: ReactNode }) => (
    <div
        {...props}
        className={`w-full relative overflow-hidden rounded-xl bg-white border border-outline flex flex-col justify-start items-stretch`}
    >
        {children}
    </div>
)

export default function VirtualizedGridList({
    isLoading,
    gridData,
    headers,
    statsType,
    threshold,
    selection,
    setPopupMessage
}: {
    isLoading: boolean,
    gridData: TableData[],
    headers: TableHeader[],
    statsType?: 'model' | 'prediction',
    threshold?: number,
    selection?: {
        selectedItems: (string | number)[],
        setSelectedItems: Dispatch<SetStateAction<(string | number)[]>>,
    },
    setPopupMessage: (popupMessage: string, isSuccessMessage: boolean) => void,

}) {
    const [ itemWidth, setItemWidth ] = useState('33%');
    const theme = useTheme();
    const extraSmallScreen = useMediaQuery(theme.breakpoints.down('xs'), { noSsr: true });
    const smallScreen = useMediaQuery(theme.breakpoints.only('sm'), { noSsr: true });
    const mediumScreen = useMediaQuery(theme.breakpoints.only('md'), { noSsr: true });
    const largeScreen = useMediaQuery(theme.breakpoints.only('lg'), { noSsr: true });
    const extraLargeScreen = useMediaQuery(theme.breakpoints.up('xl'), { noSsr: true });


    const handleResize = () => {
        let width = '33%';

        if (extraSmallScreen) {
            width = '100%';
        } else if (smallScreen) {
            width = '50%';
        } else if (mediumScreen) {
            width = '33%';
        } else if (largeScreen) {
            width = '25%'
        } else if (extraLargeScreen) {
            width = '20%'
        }

        setItemWidth(width);
    };

    useEffect(() => {
        handleResize();
        //eslint-disable-next-line
    }, [
        extraSmallScreen,
        smallScreen,
        mediumScreen,
        largeScreen,
        extraLargeScreen
    ]);

    const filterHeaders = () => {
        const shownHeaders = headers.filter(header => header.isShown);
        let filteredHeaders: TableHeader[] = [];

        shownHeaders.forEach(header => {
            if (header.value.toLocaleLowerCase().includes('smile')) {
                if (header.label === 'Structure') {
                    filteredHeaders.push(header)
                }
            } else {
                filteredHeaders.push(header);
            }
        })

        return filteredHeaders.slice(0,5)
    };

    const handleItemSelection = (e:ChangeEvent<HTMLInputElement>) => {
        if (selection) {
            const { selectedItems, setSelectedItems } = selection;
            const id = e.target.value;
            if (e.target.checked) {
                setSelectedItems(selectedItems.concat(id));
            } else {
                setSelectedItems(selectedItems.filter(item => item !== id));
            }
        }
    };

    const getMethodLink = (data: TableData) => {
        const methodField = data.fields.filter(field => field.associatedColumn === 'method');
        if (methodField.length > 0) {
            return methodField[0].action!;
        }
    }

    function itemContent(index: number, item: TableData) {
        const visField = item.fields.filter(field => field.type === 'structure' || field.type === 'plot');
        const methodLink = getMethodLink(item);
    
        return (
            <ItemWrapper key={index}>
                {selection && (
                    <div className="z-20 absolute top-0 left-0 p-7 flex justify-start items-center">
                        <CustomCheckbox
                            checked={selection.selectedItems.includes(item.id)}
                            handleChange={handleItemSelection}
                            value={item.id}
                        />
                    </div>
                )}
                {selection && selection.selectedItems.includes(item.id) && (
                    <div className="z-10 absolute top-0 left-0 right-0 bottom-0 bg-[#D9DBE7]/[0.4]"></div>
                )}
                {visField ? (
                <>
                {visField[0].type === 'structure' ? (
                    <StructureComponent 
                        value={visField[0].value as string}
                    />
                ) : (
                    <PlotComponent 
                        plotFields={visField}
                    />
                )}
                </>
                ) : (
                <></>
                )}

                <div className="w-full grow flex flex-col justify-start items-stretch gap-6 p-6 border-t border-outline">
                    {filterHeaders().map(header => {
                        const matchingData = item.fields.find(field => field.associatedColumn === header.value);
                        if (matchingData) {
                            if (matchingData.type === 'structure' || matchingData.associatedColumn === 'method') {
                                return (
                                    <div className="grow">
                                        <p className={`font-semibold text-default text-[16px] max-w-full truncate`}>
                                            {matchingData.value}
                                        </p>
                                    </div>
                                )
                            } else {
                                return (
                                    <div className="w-full flex-col justify-start items-start gap-6">
                                        <div className="flex justify-start items-center gap-2">
                                            <p className="text-[12px] font-semibold text-disable">{header.label}</p>
                                            {header.columnMetadata}
                                        </div>
                                        
                                        {matchingData.type === 'stat' && statsType !== undefined? (
                                            <StatsValue 
                                            name={matchingData.associatedColumn} 
                                            value={matchingData.value as string} 
                                            statsType={statsType}
                                            threshold={threshold}
                                        />
                                        ) : matchingData.type === 'text' ? (
                                            <p className="text-default text-[14px] font-medium">{matchingData.value}</p>
                                        ) : (
                                            <></>
                                        )}
                                        
                                    </div>
                                )
                            }
                        } else {
                            return null;
                        }
                    })}
                </div>

                {methodLink ? (
                    <button onClick={(e:any) => methodLink(e)} className="w-full bg-[#F7F6FB] text-disable font-semibold text-[12px] p-3 hover:bg-secondary hover:text-primary transition-all">
                        See more details
                    </button>
                ) : (
                    <DetailsModal 
                        data={item}
                        isGridItem
                        setPopupMessage={setPopupMessage}
                    />
                )} 
            </ItemWrapper>
        )
    }

    return (
        <VirtuosoGrid 
            style={{
                width: '100%',
                height: '100%',
                flexGrow: 1,
            }}
            components={gridComponents}
            itemContent={itemContent}
            data={gridData}
            context={{
                isLoading,
                itemWidth,
            }}
        />
    )
}